// SPDX-License-Identifier: AGPL-3.0-or-later

import {STREAM_VOLUME_DESCRIPTOR} from '@app/features/ui/action_menu/items/voice_participant_menu_data/shared';
import {MediaVerticalVolumeControl} from '@app/features/voice/components/media_player/components/MediaVerticalVolumeControl';
import {getStreamKey} from '@app/features/voice/components/StreamKeys';
import MediaEngine, {useMediaEngineVersion} from '@app/features/voice/engine/MediaEngineFacade';
import {
	asVoiceTrackSource,
	isScreenShareAudioPublicationLike,
	VoiceTrackSource,
} from '@app/features/voice/engine/VoiceTrackSource';
import ParticipantVolume from '@app/features/voice/state/ParticipantVolume';
import StreamAudioPrefs from '@app/features/voice/state/StreamAudioPrefs';
import {VOICE_USER_VOLUME_DESCRIPTOR} from '@app/features/voice/utils/VoiceMessageDescriptors';
import {parseVoiceParticipantIdentity} from '@app/features/voice/utils/VoiceParticipantIdentity';
import {VOICE_VOLUME_MAX_SLIDER_VOLUME} from '@app/features/voice/utils/VoiceVolumeUtils';
import {useLingui} from '@lingui/react/macro';
import type {TrackReferenceOrPlaceholder} from '@livekit/components-react';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback} from 'react';

interface FocusedStreamVolumeControlProps {
	track: TrackReferenceOrPlaceholder | null;
	guildId: string | null | undefined;
	channelId: string;
	className?: string;
}

/**
 * Volume for whatever tile is focused: a remote screen share drives that stream's audio, and a remote
 * camera tile or avatar placeholder drives that person's voice volume and local mute (the same
 * settings the participant context menu writes). Renders nothing for the local participant.
 */
export const FocusedStreamVolumeControl: React.FC<FocusedStreamVolumeControlProps> = observer(
	function FocusedStreamVolumeControl({track, guildId, channelId, className}) {
		const {i18n} = useLingui();
		useMediaEngineVersion();
		const isRemote = track != null && !track.participant.isLocal;
		const isRemoteScreenShare = isRemote && asVoiceTrackSource(track.source) === VoiceTrackSource.ScreenShare;
		const {userId, connectionId} = isRemote
			? parseVoiceParticipantIdentity(track.participant.identity)
			: {userId: null, connectionId: null};
		const streamKey = isRemoteScreenShare && connectionId ? getStreamKey(guildId, channelId, connectionId) : '';
		const hasStreamAudio =
			isRemoteScreenShare &&
			[...track.participant.audioTrackPublications.values()].some((publication) =>
				isScreenShareAudioPublicationLike(publication),
			);
		const isMuted = streamKey
			? StreamAudioPrefs.isMuted(streamKey)
			: userId
				? ParticipantVolume.isLocalMuted(userId)
				: false;
		const handleToggleMute = useCallback(() => {
			if (!userId) return;
			if (streamKey) {
				StreamAudioPrefs.setMuted(streamKey, !StreamAudioPrefs.isMuted(streamKey));
			} else {
				ParticipantVolume.setLocalMute(userId, !ParticipantVolume.isLocalMuted(userId));
			}
			MediaEngine.applyLocalAudioPreferencesForUser(userId);
		}, [streamKey, userId]);
		const handleVolumeChange = useCallback(
			(volume: number) => {
				if (!userId) return;
				if (streamKey) {
					StreamAudioPrefs.setVolume(streamKey, Math.round(volume * 100));
				} else {
					ParticipantVolume.setVolume(userId, Math.round(volume * 100));
				}
				MediaEngine.applyLocalAudioPreferencesForUser(userId);
			},
			[streamKey, userId],
		);
		if (!userId || !connectionId) return null;
		if (isRemoteScreenShare && !hasStreamAudio) return null;
		return (
			<MediaVerticalVolumeControl
				volume={(streamKey ? StreamAudioPrefs.getVolume(streamKey) : ParticipantVolume.getVolume(userId)) / 100}
				isMuted={isMuted}
				maxVolume={VOICE_VOLUME_MAX_SLIDER_VOLUME}
				onVolumeChange={handleVolumeChange}
				onToggleMute={handleToggleMute}
				iconSize={18}
				className={className}
				position="above"
				ariaLabel={i18n._(streamKey ? STREAM_VOLUME_DESCRIPTOR : VOICE_USER_VOLUME_DESCRIPTOR)}
				data-flx="voice.focused-stream-volume-control.media-vertical-volume-control"
			/>
		);
	},
);
